import { documentToHtmlString, type Options } from '@contentful/rich-text-html-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';

import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import {
	accordionContentType,
	parseAccordionEmbeddedEntry,
} from '../../../uniform/components/accordion/src/parse-accordion-embedded-entry.ts';
import {
	accordionsSectionContentType,
	parseAccordionsSectionEmbeddedEntry,
} from '../../../uniform/components/accordions-section/src/parse-accordions-section-embedded-entry.ts';
import {
	pricingTiersTableContentType,
	parsePricingTiersTableEmbeddedEntry,
} from '../../../uniform/components/pricing-tiers-table/src/parse-pricing-tiers-table-embedded-entry.ts';

export const richTextToHTML = (richText: Document) => {
	let htmlString: string = documentToHtmlString(richText, getRichTextRenderOptions());

	htmlString = htmlString.replace(/\n/g, '<br />');

	return htmlString;
};

const DEFAULT_STRIP_KEYS = new Set(['sys', 'metadata', 'createdAt', 'updatedAt']);

export interface StripContentfulMetadataOptions {
	keys?: Set<string> | string[];
}
export const stripContentfulMetadata = (
	data: unknown,
	options: StripContentfulMetadataOptions = {},
): void => {
	const keysToStrip =
		options.keys !== null && options.keys !== undefined
			? new Set(options.keys)
			: DEFAULT_STRIP_KEYS;

	const walk = (node: unknown): void => {
		if (Array.isArray(node)) {
			node.forEach(walk);
		} else if (node !== null && typeof node === 'object') {
			const obj = node as Record<string, unknown>;
			for (const key of keysToStrip) {
				delete obj[key];
			}
			for (const value of Object.values(obj)) {
				walk(value);
			}
		}
	};

	walk(data);
};

export const getShouldStripExcessData = () => {
	return checkFeatureFlagGate(featureFlags.waePricingStripExcessData);
};

interface EmbeddedEntryParserArgs {
	fields: unknown;
	renderRichText: (document: Document) => string;
}

type EmbeddedEntryParser = (args: EmbeddedEntryParserArgs) => string | undefined;

const embeddedEntryParsers: Record<string, EmbeddedEntryParser> = {
	[accordionContentType]: parseAccordionEmbeddedEntry,
	[accordionsSectionContentType]: parseAccordionsSectionEmbeddedEntry,
	[pricingTiersTableContentType]: parsePricingTiersTableEmbeddedEntry,
};

function getRichTextRenderOptions(): Options | undefined {
	if (!checkFeatureFlagGate(featureFlags.waeRichTextEmbeddedComponents)) {
		return undefined;
	}

	return {
		renderNode: {
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const target = node.data['target'] as {
					sys?: { contentType?: { sys?: { id?: string } } };
					fields?: unknown;
				};
				const contentTypeId = target?.sys?.contentType?.sys?.id;
				if (!contentTypeId) {
					return '<div></div>';
				}
				const parseEmbeddedEntry = embeddedEntryParsers[contentTypeId];
				return (
					parseEmbeddedEntry?.({ fields: target?.fields, renderRichText: richTextToHTML }) ??
					'<div></div>'
				);
			},
		},
	};
}
